import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import { serverOAuthHeaders } from 'new/utils/serverOAuthHeaders';

import * as actionTypes from '../../constants/actionTypes';
import { CALL_PRIVATE_API } from '../../middleware/api';

import { GET } from '../../constants/requestMethods';
import {
  vendorAuthParamSchema,
  vendorEndpointParamSchema,
  vendorEndpointSchema,
  vendorSchema,
} from '../../schemas';

import LocalStorageUtils from '../../lib/LocalStorageUtils';

import { isStateOneDayOld } from '../../lib/StateUtils';

export const loadFullVendor = (key) => (dispatch, getState) => {
  const state = getState();
  const {
    entities: { vendors },
    paging: { connAuthById },
  } = state;

  // Decided once for the whole exchange: the request below and the follow-up decision must
  // agree about whether this client opted in, even if the flag flips in between.
  const optInHeaders = serverOAuthHeaders();

  if (Object.values(vendors).some((vendor) => vendor.name === key)) {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_FULL_VENDOR_REQUEST,
          actionTypes.GET_FULL_VENDOR_SUCCESS,
          actionTypes.GET_FULL_VENDOR_FAILURE,
        ],
        endpoint: `/vendors?vendor_name=${key}&expand=1`,
        schema: vendorSchema,
        requestMethod: GET,
        ignoreErrors: true,
        // This response carries auth_parameters inline via auth_templates, and for
        // templatized-API connectors it is the only place they arrive.
        ...(optInHeaders && { extraHeaders: optInHeaders }),
      },
    }).then((response) => {
      const vendor = vendors[response.result];
      const auth = connAuthById[vendor.name];
      const depends = vendor?.config?.dependsOnVendors;
      const calls = [];
      if (depends && depends.length) {
        depends.forEach((vendorName) => {
          calls.push(dispatch(loadFullVendor(vendorName)));
        });
      }
      // Opted in, the expanded response above already carries auth_parameters nested under
      // auth_templates and this fetch would only re-deliver the same set.
      //
      // NOT opted in, it is still the only source: `OauthParamFilter.show_auth_templates?`
      // omits `auth_templates` from the vendor response entirely for the seven migrated core
      // connectors unless the header is present, so the expand carries nothing for them. The
      // dedicated endpoint serves the legacy client id/secret in that state, which the
      // client-side services still read. Removing it unconditionally breaks flag-off.
      if (auth?.fetchAuthParams && !optInHeaders) {
        calls.push(dispatch(loadVendorAuths(vendor)));
      }
      return Promise.all(calls);
    });
  }
  // returning this to simulate a thunk return
  return Promise.resolve();
};

export const loadVendors =
  (forceUpdate = false) =>
  (dispatch, getState) => {
    // check if vendors > 1 day old
    const {
      vendors: { vendors },
    } = getState();
    const shouldFetchVendors =
      forceUpdate ||
      isNil(vendors) ||
      isNil(vendors.lastUpdated) ||
      isStateOneDayOld(vendors.lastUpdated);

    if (!shouldFetchVendors) {
      return;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_VENDORS_REQUEST,
          actionTypes.GET_VENDORS_SUCCESS,
          actionTypes.GET_VENDORS_FAILURE,
        ],
        endpoint: `/vendors`,
        schema: [vendorSchema],
        requestMethod: GET,
      },
    }).then(() => {
      //store vendors in local storage to avoid crashing the page if user loads one of the edit forms
      const {
        entities: { vendors: vendorEntities },
        vendors: { vendors },
      } = getState();
      const localStorage = LocalStorageUtils.getObject('nexSession');

      const updatedLocalStorage = merge(
        {
          entities: { vendors: vendorEntities },
          vendors: { vendors: vendors },
        },
        localStorage,
      );

      LocalStorageUtils.saveObject('nexSession', updatedLocalStorage);
      dispatch(loadGlobalResourceParameters());
    });
  };

// Still the only source of auth_parameters for the seven migrated core connectors when the
// client has not opted in — see `loadFullVendor`. `extraHeaders` defaults to the opt-in header
// so the four containers that wire this into mapDispatchToProps stay correct either way.
export const loadVendorAuths =
  (vendor, extraHeaders = serverOAuthHeaders()) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_VENDOR_AUTH_PARAMS_REQUEST,
          actionTypes.GET_VENDOR_AUTH_PARAMS_SUCCESS,
          actionTypes.GET_VENDOR_AUTH_PARAMS_FAILURE,
        ],
        endpoint: `/auth_parameters?vendor_name=${vendor.name}`,
        schema: [vendorAuthParamSchema],
        requestMethod: GET,
        ...(extraHeaders && { extraHeaders }),
        requestInfo: {
          vendors: [vendor],
        },
      },
    });
  };

export const loadGlobalResourceParameters = () => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_VENDOR_ENDPOINT_PARAMS_REQUEST,
        actionTypes.GET_VENDOR_ENDPOINT_PARAMS_SUCCESS,
        actionTypes.GET_VENDOR_ENDPOINT_PARAMS_FAILURE,
      ],
      endpoint: `/resource_parameters?global=true`,
      schema: [vendorEndpointParamSchema],
      requestMethod: GET,
    },
  });
};
